<template>
  <Dialog :visible="visible" :header="field ? 'Edit custom field' : 'New custom field'" modal
    :style="{ width: '34rem', maxWidth: '95vw' }" @update:visible="onVisibility">
    <form class="flex flex-col gap-4" @submit.prevent="onSubmit">
      <div class="flex flex-col gap-1.5">
        <label for="cf-label" class="text-sm font-semibold">Label</label>
        <InputText id="cf-label" v-model="form.label" :invalid="!!labelError" placeholder="e.g. Blood Group"
          data-testid="custom-field-label" />
        <small v-if="labelError" class="text-red-500" data-testid="custom-field-label-error">{{ labelError }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="cf-key" class="text-sm font-semibold">Key</label>
        <InputText id="cf-key" v-model="form.key" :disabled="!!field" :invalid="!!keyError"
          :placeholder="keyPreview || 'derived from the label'" class="font-mono" data-testid="custom-field-key" />
        <small v-if="keyError" class="text-red-500" data-testid="custom-field-key-error">{{ keyError }}</small>
        <small v-else class="text-surface-500">
          {{ keyHintText }}
        </small>
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="cf-type" class="text-sm font-semibold">Type</label>
        <Select inputId="cf-type" v-model="form.type" :options="typeOptions" optionLabel="label" optionValue="value"
          :disabled="typeLocked" :invalid="!!typeError" data-testid="custom-field-type">
          <template #option="{ option }">
            <div class="flex flex-col">
              <span>{{ option.label }}</span>
              <small class="text-surface-500">{{ option.hint }}</small>
            </div>
          </template>
        </Select>
        <small v-if="typeError" class="text-red-500">{{ typeError }}</small>
        <small v-else-if="typeLocked" class="text-surface-500">
          The type is locked because {{ field?.values_count }} record(s) already hold a value.
        </small>
      </div>

      <div v-if="form.type === CustomFieldType.Select" class="flex flex-col gap-1.5">
        <label for="cf-choices" class="text-sm font-semibold">Choices</label>
        <Select inputId="cf-choices" v-model="choices" :options="choicesOptions" optionLabel="label" optionValue="value"
          :disabled="typeLocked" :invalid="!!optionsSourceError" data-testid="custom-field-choices">
          <template #option="{ option }">
            <div class="flex flex-col">
              <span>{{ option.label }}</span>
              <small class="text-surface-500">{{ option.hint }}</small>
            </div>
          </template>
        </Select>
        <small v-if="optionsSourceError" class="text-red-500" data-testid="custom-field-choices-error">{{ optionsSourceError }}</small>
        <small v-else-if="typeLocked" class="text-surface-500">The choices are locked because records already hold a value.</small>
      </div>

      <div v-if="form.type === CustomFieldType.Select && form.options_source === CustomFieldOptionsSource.NigerianLgas"
        class="flex flex-col gap-1.5">
        <label for="cf-depends-on" class="text-sm font-semibold">Follows <span class="font-normal text-surface-500">(optional)</span></label>
        <Select inputId="cf-depends-on" v-model="form.depends_on" :options="stateFieldOptions" optionLabel="label"
          optionValue="key" showClear placeholder="No field: offer every LGA" :invalid="!!dependsOnError"
          data-testid="custom-field-depends-on" />
        <small v-if="dependsOnError" class="text-red-500" data-testid="custom-field-depends-on-error">{{ dependsOnError }}</small>
        <small v-else-if="stateFieldOptions.length" class="text-surface-500">Only the LGAs of the state chosen in that field are offered.</small>
        <small v-else class="text-surface-500">Add a dropdown of Nigerian states first to make the LGAs follow it.</small>
      </div>

      <div v-if="form.type === CustomFieldType.Select && !form.options_source" class="flex flex-col gap-2" data-testid="custom-field-options">
        <label class="text-sm font-semibold">Options</label>
        <div v-for="(option, index) in form.options" :key="index" class="flex items-center gap-2">
          <InputText v-model="option.label" :placeholder="`Option ${index + 1}`" class="flex-1"
            :invalid="!!optionsError" :data-testid="`custom-field-option-${index}`" />
          <Button icon="pi pi-trash" severity="secondary" text rounded aria-label="Remove option"
            :disabled="form.options.length === 1" @click="removeOption(index)" />
        </div>
        <Button label="Add option" icon="pi pi-plus" text size="small" class="self-start" @click="addOption"
          data-testid="custom-field-add-option" />
        <small v-if="optionsError" class="text-red-500" data-testid="custom-field-options-error">{{ optionsError }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="cf-description" class="text-sm font-semibold">Help text <span class="font-normal text-surface-500">(optional)</span></label>
        <Textarea id="cf-description" v-model="form.description" rows="2" maxlength="500" autoResize
          :invalid="!!descriptionError" placeholder="Shown under the input on the student profile" />
        <small v-if="descriptionError" class="text-red-500">{{ descriptionError }}</small>
      </div>

      <div v-if="forStudents" class="flex flex-col gap-1.5">
        <label for="cf-student-access" class="text-sm font-semibold">Students can</label>
        <Select inputId="cf-student-access" v-model="form.student_access" :options="studentAccessOptions" optionLabel="label"
          optionValue="value" :invalid="!!studentAccessError" data-testid="custom-field-student-access">
          <template #option="{ option }">
            <div class="flex flex-col">
              <span>{{ option.label }}</span>
              <small class="text-surface-500">{{ option.hint }}</small>
            </div>
          </template>
        </Select>
        <small v-if="studentAccessError" class="text-red-500" data-testid="custom-field-student-access-error">{{ studentAccessError }}</small>
        <Message v-else-if="studentAccessHint" severity="warn" size="small" variant="simple" data-testid="custom-field-student-access-note">
          {{ studentAccessHint }}
        </Message>
        <small v-else class="text-surface-500">Students who fill it in save straight to their record; staff see who filled it in.</small>
      </div>

      <div class="flex flex-wrap gap-6">
        <div class="flex items-center gap-2">
          <ToggleSwitch inputId="cf-required" v-model="form.is_required" data-testid="custom-field-required" />
          <label for="cf-required" class="text-sm">Required</label>
        </div>
        <div class="flex items-center gap-2">
          <ToggleSwitch inputId="cf-active" v-model="form.is_active" data-testid="custom-field-active" />
          <label for="cf-active" class="text-sm">Active</label>
        </div>
      </div>
      <small class="text-surface-500 -mt-2">Retired (inactive) fields are hidden from {{ entityCopy.noun }} profiles but keep their saved values.</small>
    </form>

    <template #footer>
      <Button label="Cancel" severity="secondary" text @click="close" data-testid="custom-field-cancel" />
      <Button label="Save" icon="pi pi-check" :loading="saving" @click="onSubmit" data-testid="custom-field-save" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength, required } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { CustomFieldEntity, CustomFieldOptionsSource, CustomFieldType } from '@/types/composition-functions';
import type { CustomFieldFieldsFragment } from '@/types/operations';
import { useFormErrors } from '@/composables/useFormErrors';
import { useCustomFieldMutations } from '@/composables/mutations/useCustomFieldMutations';
import {
  CUSTOM_FIELD_CHOICES_OPTIONS, CUSTOM_FIELD_KEY_PATTERN, CUSTOM_FIELD_TYPE_OPTIONS, OWN_OPTIONS_LIST, buildCreateInput,
  buildUpdateInput, cleanOptions, customFieldEntityCopy, customFieldFormFrom, customFieldKeyFromLabel, customFieldKeyPreview,
  emptyCustomFieldForm, stateFieldChoices, type CustomFieldChoicesValue,
} from '@/utils/customFields';
import { STUDENT_ACCESS_OPTIONS, studentAccessNote } from '@/utils/studentProfileForm';

const props = defineProps<{
  visible: boolean;
  /** The field to edit; null creates a new one. */
  field: CustomFieldFieldsFragment | null;
  entity?: CustomFieldEntity;
  /** The record type's existing fields, for the state field an LGA dropdown can follow. */
  fields?: CustomFieldFieldsFragment[];
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'saved'): void;
}>();

const toast = useToast();
const { create, update } = useCustomFieldMutations();
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

const form = reactive(emptyCustomFieldForm());
const saving = ref(false);
const typeOptions = [...CUSTOM_FIELD_TYPE_OPTIONS];
const choicesOptions = [...CUSTOM_FIELD_CHOICES_OPTIONS];
const studentAccessOptions = [...STUDENT_ACCESS_OPTIONS];
const forStudents = computed(() => (props.entity ?? CustomFieldEntity.Student) === CustomFieldEntity.Student);

/** The choices picker works with a sentinel for "your own list"; the form keeps null. */
const choices = computed<CustomFieldChoicesValue>({
  get: () => form.options_source ?? OWN_OPTIONS_LIST,
  set: (value) => { form.options_source = value === OWN_OPTIONS_LIST ? null : value; },
});
const stateFieldOptions = computed(() => stateFieldChoices(props.fields ?? [], props.field?.key));

const typeLocked = computed(() => !!props.field && props.field.values_count > 0);
const keyPreview = computed(() => customFieldKeyFromLabel(form.label));
const keyHintText = computed(() => {
  if (props.field) return `The key cannot change. ${entityCopy.value.keyHint}`;
  const preview = customFieldKeyPreview(form.label);
  return `${entityCopy.value.keyHint} Leave blank and it ${preview === 'will be generated' ? preview : `will be ${preview}`}.`;
});
const entityCopy = computed(() => customFieldEntityCopy(props.entity ?? CustomFieldEntity.Student));

const rules = computed(() => ({
  label: { required: helpers.withMessage('Enter a label for the field.', required), maxLength: maxLength(120) },
  key: {
    pattern: helpers.withMessage(
      'Use lowercase letters, numbers and underscores, starting with a letter.',
      (value: string) => !!props.field || value.trim() === '' || CUSTOM_FIELD_KEY_PATTERN.test(value.trim()),
    ),
  },
  options: {
    atLeastOne: helpers.withMessage(
      'Add at least one option for a dropdown field.',
      () => form.type !== CustomFieldType.Select || !!form.options_source || (cleanOptions(form)?.length ?? 0) > 0,
    ),
  },
}));
const v$ = useVuelidate(rules, form);

const firstError = (key: 'label' | 'key' | 'options') => v$.value[key].$errors[0]?.$message as string | undefined;
const labelError = computed(() => firstError('label') || getErrorMessage('label'));
const keyError = computed(() => firstError('key') || getErrorMessage('key'));
const optionsError = computed(() => firstError('options') || getErrorMessage('options'));
const typeError = computed(() => getErrorMessage('type'));
const descriptionError = computed(() => getErrorMessage('description'));
const optionsSourceError = computed(() => getErrorMessage('options_source'));
const dependsOnError = computed(() => getErrorMessage('depends_on'));
const studentAccessError = computed(() => getErrorMessage('student_access'));
const studentAccessHint = computed(() => studentAccessNote(
  { key: props.field?.key ?? '', label: form.label, student_access: form.student_access, depends_on: form.depends_on, is_active: form.is_active },
  props.fields ?? [],
));

watch(() => [props.visible, props.field] as const, ([visible]) => {
  if (!visible) return;
  Object.assign(form, props.field ? customFieldFormFrom(props.field) : emptyCustomFieldForm());
  clearErrors();
  v$.value.$reset();
}, { immediate: true });

function addOption() {
  form.options.push({ label: '', value: '' });
}

function removeOption(index: number) {
  form.options.splice(index, 1);
}

function onVisibility(value: boolean) {
  emit('update:visible', value);
}

function close() {
  emit('update:visible', false);
}

async function onSubmit() {
  clearErrors();
  if (!(await v$.value.$validate())) return;

  saving.value = true;
  try {
    if (props.field) {
      await update.mutate({ id: props.field.id, input: buildUpdateInput(form) });
    } else {
      await create.mutate({ input: buildCreateInput(form, props.entity ?? CustomFieldEntity.Student) });
    }
    toast.add({ severity: 'success', summary: 'Saved', detail: `"${form.label.trim()}" saved.`, life: 3000 });
    emit('saved');
    close();
  } catch (error) {
    handleServerErrors(error);
    const inline = [labelError, keyError, optionsError, typeError, descriptionError, optionsSourceError, dependsOnError, studentAccessError];
    if (!inline.some(e => !!e.value)) {
      console.error(error);
      toast.add({ severity: 'error', summary: 'Could not save', detail: (error as Error)?.message || 'Please try again.', life: 5000 });
    }
  } finally {
    saving.value = false;
  }
}
</script>
